/**
 * Data hook for the KPI Scorecard summary endpoint.
 * Calls GET /api/pm/scorecard/. Fixture substitution (dev without a running
 * backend) only happens when VITE_SCORECARD_FIXTURE=true — a real network
 * error in production always surfaces as `error`, never as fabricated data.
 */
import { useEffect, useState } from 'react';

import { scorecard as scorecardApi } from '@/helpers/ApiHelper';
import { SCORECARD_FIXTURE } from '@/pages/scorecard/scorecard.fixture';
import type { ScorecardParams, ScorecardResponse } from '@/types/scorecard';

const USE_FIXTURE = import.meta.env.VITE_SCORECARD_FIXTURE === 'true';

export function useScorecard(params: ScorecardParams = {}) {
  const [data, setData] = useState<ScorecardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // Bumped by refetch() to re-run the effect (e.g. after a manual sync) without
  // changing the period params.
  const [reloadNonce, setReloadNonce] = useState(0);
  const refetch = () => setReloadNonce((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    if (USE_FIXTURE) {
      // Simulate async for consistent loading-state behavior in dev.
      const tid = setTimeout(() => {
        if (!cancelled) {
          setData(SCORECARD_FIXTURE);
          setError(null);
          setIsLoading(false);
        }
      }, 300);
      return () => {
        cancelled = true;
        clearTimeout(tid);
      };
    }

    scorecardApi
      .getScorecard(params)
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          // Fixture substitution is gated on the explicit dev flag ONLY.
          // Never fall back silently in prod — a real network error (Cloud Run
          // cold start, transient CORS/backend blip, etc.) must surface as an
          // error with data left null, not render fabricated KPI numbers.
          if (USE_FIXTURE) {
            setData(SCORECARD_FIXTURE);
            setError(null);
          } else {
            setError(e instanceof Error ? e : new Error(String(e)));
          }
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.preset, params.start, params.end, reloadNonce]);

  return { data, isLoading, error, refetch };
}
